import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosClient from '../../api/axiosClient';
import { useAuth } from '../../context/AuthContext';
import ReportWhatsAppModal from '../../components/ReportWhatsAppModal';

const OutstandingReport = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = (user?.role || '').toLowerCase() === 'admin';

  const [accounts, setAccounts] = useState([]);
  const [summary, setSummary] = useState({
    totalReceivable: 0,
    totalPayable: 0,
    netBalancePosition: 0,
    customerCount: 0,
    supplierCount: 0,
    totalAccounts: 0
  });
  const [firms, setFirms] = useState([]);
  const [selectedFirm, setSelectedFirm] = useState(isAdmin ? 'ALL' : (user?.firmId || '1'));
  const [typeFilter, setTypeFilter] = useState('ALL'); // ALL, CUSTOMER, SUPPLIER
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);

  // Individual Reminder State
  const [reminderModal, setReminderModal] = useState({
    show: false,
    account: null,
    mobile: '',
    customNote: '',
    sending: false,
    statusMsg: ''
  });

  useEffect(() => {
    const fetchFirms = async () => {
      try {
        const configRes = await axiosClient.get('/auth/config');
        if (configRes.success) setFirms(configRes.data?.firms || []);
      } catch (e) {
        console.error(e);
      }
    };
    if (isAdmin) fetchFirms();
  }, [isAdmin]);

  const fetchOutstandingReport = async () => {
    setLoading(true);
    try {
      const firmParam = isAdmin ? (selectedFirm || 'ALL') : (user?.firmId || '');
      const typeParam = typeFilter;
      const searchParam = searchQuery;
      const res = await axiosClient.get(
        `/reports/outstanding?firmId=${firmParam}&type=${typeParam}&search=${encodeURIComponent(searchParam)}`
      );
      if (res.success) {
        setAccounts(res.data || []);
        if (res.summary) setSummary(res.summary);
      }
    } catch (e) {
      console.error('Error fetching outstanding report:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOutstandingReport();
  }, [selectedFirm, typeFilter, searchQuery]);

  const openIndividualReminder = (acc) => {
    const net = parseFloat(acc.netBalance) || 0;
    const absVal = Math.abs(net).toFixed(2);
    const pos = net > 0 ? 'Receivable Outstanding' : 'Payable Credit';
    const firmName = (selectedFirm !== 'ALL' && firms.find(f => String(f.Id) === String(selectedFirm))?.firmName) || user?.firmName || 'Fish Shop Mumbai';

    const defaultMsg = `Dear *${acc.AccountName}*,\n\nYour current balance with *${firmName}* is *₹${absVal}* (${pos}).\n\nKindly check your account statement or arrange payment at your earliest convenience. Thank you!`;

    setReminderModal({
      show: true,
      account: acc,
      mobile: acc.MobileNo || '',
      customNote: defaultMsg,
      sending: false,
      statusMsg: ''
    });
  };

  const handleSendReminderWeb = () => {
    if (!reminderModal.mobile) {
      alert('Please enter a mobile number.');
      return;
    }
    let clean = reminderModal.mobile.replace(/\D/g, '');
    if (clean.length === 10) clean = '91' + clean;

    const url = `https://api.whatsapp.com/send?phone=${clean}&text=${encodeURIComponent(reminderModal.customNote)}`;
    window.open(url, '_blank');
  };

  const handleSendReminderApi = async () => {
    if (!reminderModal.mobile) {
      alert('Please enter a mobile number.');
      return;
    }
    setReminderModal((prev) => ({ ...prev, sending: true, statusMsg: '' }));
    try {
      const res = await axiosClient.post('/whatsapp/send-outstanding', {
        accountId: reminderModal.account?.Id,
        phone: reminderModal.mobile,
        customNote: reminderModal.customNote
      });
      if (res.success) {
        setReminderModal((prev) => ({
          ...prev,
          sending: false,
          statusMsg: '✅ Reminder sent via WhatsApp Business API successfully!'
        }));
      } else {
        setReminderModal((prev) => ({
          ...prev,
          sending: false,
          statusMsg: `⚠️ ${res.message || 'Failed to send reminder via API.'}`
        }));
      }
    } catch (err) {
      setReminderModal((prev) => ({
        ...prev,
        sending: false,
        statusMsg: `⚠️ ${err.message || 'Error dispatching API reminder.'}`
      }));
    }
  };

  return (
    <div className="container-fluid p-0">
      {/* Top Header & Actions */}
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2 no-print">
        <div>
          <h3 className="fw-bold mb-0 text-dark">
            <i className="bi bi-pie-chart-fill text-danger me-2"></i> Outstanding Balances Report
          </h3>
          <p className="text-muted small mb-0">
            {isAdmin 
              ? 'Multi-Firm Receivables & Payables Ledger Summary' 
              : `Active Outstanding Balances for: ${user?.firmName || 'Assigned Branch'}`}
          </p>
        </div>

        <div className="d-flex align-items-center gap-2 flex-wrap">
          {isAdmin && (
            <div className="d-flex align-items-center gap-1 bg-light border p-1 rounded">
              <i className="bi bi-building text-warning ms-1"></i>
              <select
                className="form-select form-select-sm border-0 bg-transparent"
                style={{ width: '200px' }}
                value={selectedFirm}
                onChange={(e) => setSelectedFirm(e.target.value)}
              >
                <option value="ALL">★ All Firms (Consolidated)</option>
                {firms.map((f) => (
                  <option key={f.Id} value={f.Id}>
                    {f.firmName}
                  </option>
                ))}
              </select>
            </div>
          )}

          <button
            className="btn btn-success d-flex align-items-center gap-1"
            onClick={() => setShowWhatsAppModal(true)}
            disabled={accounts.length === 0}
          >
            <i className="bi bi-whatsapp"></i> Share Summary WhatsApp
          </button>

          <button
            className="btn btn-primary d-flex align-items-center gap-1"
            onClick={() => window.print()}
          >
            <i className="bi bi-printer me-1"></i> Print / PDF Report
          </button>
        </div>
      </div>

      {/* Summary Metric Cards */}
      <div className="row g-3 mb-4 no-print">
        <div className="col-12 col-sm-6 col-lg-3">
          <div className="card shadow-sm border-0 border-start border-4 border-danger h-100">
            <div className="card-body p-3">
              <div className="text-uppercase small fw-bold text-muted mb-1">Total Customer Receivables</div>
              <h4 className="fw-bold text-danger mb-0">
                ₹{parseFloat(summary.totalReceivable || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
              </h4>
              <div className="small text-muted mt-1">
                <i className="bi bi-arrow-down-left-circle text-danger me-1"></i>
                {summary.customerCount || 0} Customers Pending
              </div>
            </div>
          </div>
        </div>

        <div className="col-12 col-sm-6 col-lg-3">
          <div className="card shadow-sm border-0 border-start border-4 border-warning h-100">
            <div className="card-body p-3">
              <div className="text-uppercase small fw-bold text-muted mb-1">Total Supplier Payables</div>
              <h4 className="fw-bold text-warning text-dark mb-0">
                ₹{parseFloat(summary.totalPayable || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
              </h4>
              <div className="small text-muted mt-1">
                <i className="bi bi-arrow-up-right-circle text-warning me-1"></i>
                {summary.supplierCount || 0} Suppliers Pending
              </div>
            </div>
          </div>
        </div>

        <div className="col-12 col-sm-6 col-lg-3">
          <div className="card shadow-sm border-0 border-start border-4 border-info h-100">
            <div className="card-body p-3">
              <div className="text-uppercase small fw-bold text-muted mb-1">Net Balance Position</div>
              <h4 className={`fw-bold mb-0 ${summary.netBalancePosition >= 0 ? 'text-success' : 'text-danger'}`}>
                ₹{parseFloat(summary.netBalancePosition || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
              </h4>
              <div className="small text-muted mt-1">
                <i className={`bi ${summary.netBalancePosition >= 0 ? 'bi-check-circle-fill text-success' : 'bi-exclamation-triangle-fill text-danger'} me-1`}></i>
                {summary.netBalancePosition >= 0 ? 'Receivable Surplus' : 'Payable Deficit'}
              </div>
            </div>
          </div>
        </div>

        <div className="col-12 col-sm-6 col-lg-3">
          <div className="card shadow-sm border-0 border-start border-4 border-secondary h-100">
            <div className="card-body p-3">
              <div className="text-uppercase small fw-bold text-muted mb-1">Active Ledger Accounts</div>
              <h4 className="fw-bold text-dark mb-0">{summary.totalAccounts || accounts.length}</h4>
              <div className="small text-muted mt-1">
                Filtered: {accounts.length} Parties Displayed
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filter and Search Bar */}
      <div className="card shadow-sm border-0 mb-3 no-print">
        <div className="card-body p-3">
          <div className="row g-2 align-items-center">
            <div className="col-12 col-md-5">
              <div className="input-group">
                <span className="input-group-text bg-white text-muted">
                  <i className="bi bi-search"></i>
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search account name, mobile or address..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                {searchQuery && (
                  <button className="btn btn-outline-secondary" onClick={() => setSearchQuery('')}>
                    <i className="bi bi-x-lg"></i>
                  </button>
                )}
              </div>
            </div>

            <div className="col-12 col-md-7 d-flex justify-content-md-end gap-2 flex-wrap">
              <div className="btn-group" role="group">
                <button
                  type="button"
                  className={`btn btn-sm ${typeFilter === 'ALL' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setTypeFilter('ALL')}
                >
                  All Accounts
                </button>
                <button
                  type="button"
                  className={`btn btn-sm ${typeFilter === 'CUSTOMER' ? 'btn-danger' : 'btn-outline-danger'}`}
                  onClick={() => setTypeFilter('CUSTOMER')}
                >
                  Receivables (Customers)
                </button>
                <button
                  type="button"
                  className={`btn btn-sm ${typeFilter === 'SUPPLIER' ? 'btn-warning text-dark' : 'btn-outline-warning text-dark'}`}
                  onClick={() => setTypeFilter('SUPPLIER')}
                >
                  Payables (Suppliers)
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Data Table */}
      <div className="card shadow-sm border-0 print-area">
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th style={{ width: '50px' }} className="text-center">#</th>
                  <th>Account / Party Name</th>
                  <th className="text-center">Type</th>
                  <th className="text-center">Mobile No</th>
                  <th>Address</th>
                  <th className="text-end">Total Billed (₹)</th>
                  <th className="text-end">Total Paid (₹)</th>
                  <th className="text-end">Outstanding Balance (₹)</th>
                  <th className="text-center no-print" style={{ width: '180px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="9" className="text-center py-5">
                      <div className="spinner-border text-primary" role="status"></div>
                      <div className="small text-muted mt-2">Calculating account ledger balances...</div>
                    </td>
                  </tr>
                ) : accounts.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="text-center py-5 text-muted">
                      <i className="bi bi-check-circle fs-1 text-success d-block mb-2"></i>
                      No outstanding account balances found matching criteria.
                    </td>
                  </tr>
                ) : (
                  accounts.map((acc, idx) => {
                    const net = parseFloat(acc.netBalance) || 0;
                    const isRec = net > 0;
                    const absNet = Math.abs(net);

                    return (
                      <tr key={acc.Id}>
                        <td className="text-center text-muted small">{idx + 1}</td>
                        <td>
                          <div className="fw-bold text-dark">{acc.AccountName}</div>
                          {acc.disc && <div className="small text-muted">{acc.disc}</div>}
                        </td>
                        <td className="text-center">
                          <span className={`badge ${acc.AccountType === 'CUSTOMER' ? 'bg-info text-dark' : acc.AccountType === 'SUPPLIER' ? 'bg-secondary' : 'bg-primary'}`}>
                            {acc.AccountType || 'BOTH'}
                          </span>
                        </td>
                        <td className="text-center">
                          {acc.MobileNo ? (
                            <a href={`tel:${acc.MobileNo}`} className="text-decoration-none text-dark">
                              <i className="bi bi-telephone me-1 text-muted"></i>{acc.MobileNo}
                            </a>
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </td>
                        <td className="small text-muted">{acc.Address || '-'}</td>
                        <td className="text-end text-muted">
                          ₹{parseFloat(acc.totalBilled || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                        </td>
                        <td className="text-end text-muted">
                          ₹{parseFloat(acc.totalPaid || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                        </td>
                        <td className="text-end">
                          <span className={`badge fs-6 px-3 py-2 ${isRec ? 'bg-danger' : 'bg-warning text-dark'}`}>
                            ₹{absNet.toLocaleString('en-IN', { minimumFractionDigits: 2 })} {isRec ? '(Dr)' : '(Cr)'}
                          </span>
                        </td>
                        <td className="text-center no-print">
                          <div className="d-flex justify-content-center gap-1">
                            <button
                              className="btn btn-sm btn-outline-primary"
                              title="View Account Statement Ledger"
                              onClick={() => navigate(`/reports/statement?accountId=${acc.Id}`)}
                            >
                              <i className="bi bi-journal-text me-1"></i> Ledger
                            </button>
                            <button
                              className="btn btn-sm btn-success"
                              title="Send WhatsApp Balance Reminder"
                              onClick={() => openIndividualReminder(acc)}
                            >
                              <i className="bi bi-whatsapp"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* WhatsApp Summary Report Modal */}
      {showWhatsAppModal && (
        <ReportWhatsAppModal
          show={showWhatsAppModal}
          onClose={() => setShowWhatsAppModal(false)}
          title="Outstanding Balances Report"
          reportType="outstanding_report"
          reportData={{
            title: 'Outstanding Balances Report',
            summary,
            firmName: (selectedFirm !== 'ALL' && firms.find(f => String(f.Id) === String(selectedFirm))?.firmName) || user?.firmName || 'PUSHPAI SEA FOOD(ALIBAG)',
            accounts
          }}
        />
      )}

      {/* Individual WhatsApp Reminder Modal */}
      {reminderModal.show && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(15, 23, 42, 0.75)', zIndex: 1060 }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '12px' }}>
              <div className="modal-header bg-success text-white">
                <h5 className="modal-title fs-6 fw-semibold">
                  <i className="bi bi-whatsapp me-2"></i> Send Payment Reminder - {reminderModal.account?.AccountName}
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => setReminderModal({ show: false, account: null, mobile: '', customNote: '', sending: false, statusMsg: '' })}
                ></button>
              </div>

              <div className="modal-body p-3">
                <div className="mb-3">
                  <label className="form-label small fw-bold text-muted">WhatsApp Mobile Number</label>
                  <div className="input-group">
                    <span className="input-group-text bg-light">+91</span>
                    <input
                      type="text"
                      className="form-control fw-bold"
                      value={reminderModal.mobile}
                      onChange={(e) => setReminderModal({ ...reminderModal, mobile: e.target.value })}
                      placeholder="Enter 10-digit mobile number"
                    />
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label small fw-bold text-muted">Reminder Message</label>
                  <textarea
                    className="form-control font-monospace small"
                    rows={5}
                    value={reminderModal.customNote}
                    onChange={(e) => setReminderModal({ ...reminderModal, customNote: e.target.value })}
                  />
                </div>

                {reminderModal.statusMsg && (
                  <div className="alert alert-info small py-2 mb-0">
                    {reminderModal.statusMsg}
                  </div>
                )}
              </div>

              <div className="modal-footer bg-light px-3 py-2 d-flex justify-content-between">
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => setReminderModal({ show: false, account: null, mobile: '', customNote: '', sending: false, statusMsg: '' })}
                >
                  Cancel
                </button>

                <div className="d-flex gap-2">
                  <button
                    type="button"
                    className="btn btn-success btn-sm d-flex align-items-center gap-1"
                    onClick={handleSendReminderWeb}
                  >
                    <i className="bi bi-whatsapp"></i> WhatsApp Web
                  </button>

                  <button
                    type="button"
                    className="btn btn-primary btn-sm d-flex align-items-center gap-1"
                    onClick={handleSendReminderApi}
                    disabled={reminderModal.sending}
                  >
                    {reminderModal.sending ? 'Sending...' : 'Send API Reminder'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OutstandingReport;
